import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import { ArrowLeft, Calendar, ChevronDown, Save, Loader2, Download, FileText } from 'lucide-react';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import ReferenceListModal from '../components/ReferenceListModal';
import StaffPhoto from '../components/StaffPhoto';
import { ROW_HIGHLIGHT_COLORS, TEXT_HIGHLIGHT_COLORS } from '../constants/rowHighlightColors';

/** Reference list backing the Remarks column — management keeps its own. */
const REMARKS_CATEGORY = 'management_remarks';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface DailyRow {
  date:       string;   // "1-May-26"  — display only, not editable
  day:        string;   // "Friday"    — display only, not editable
  department: string;
  remarks:    string;
  shift_type: string;
  scale:      number;
  per_hr:     number;
  no_hrs:     number;
  salary:     number;
  adv_date:   string;
  tid:        string;
  adv_amount: number;
  // Freeform row highlight, hex color (e.g. '#fecaca') picked via the
  // row-selection toolbar — null/'' = no highlight.
  row_color:  string | null;
  // Font color for the Remarks text specifically — independent of row_color,
  // picked from the same toolbar. null/'' = default text color.
  remarks_color: string | null;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const MONTH_ABBR = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const DAY_NAMES  = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];
const MONTHS = [
  { val: 1,  label: 'January'   }, { val: 2,  label: 'February'  },
  { val: 3,  label: 'March'     }, { val: 4,  label: 'April'     },
  { val: 5,  label: 'May'       }, { val: 6,  label: 'June'      },
  { val: 7,  label: 'July'      }, { val: 8,  label: 'August'    },
  { val: 9,  label: 'September' }, { val: 10, label: 'October'   },
  { val: 11, label: 'November'  }, { val: 12, label: 'December'  },
];
const YEARS = [2024, 2025, 2026, 2027];

/**
 * The number columns. Doubles as the set that fills across a multi-row
 * drag-selection — every numeric cell is bulk-editable, while the text columns
 * (Department, Remarks, Shift Type, Adv Date, TID) stay strictly per-row.
 */
const NUMERIC_FIELDS: (keyof DailyRow)[] = ['scale', 'per_hr', 'no_hrs', 'salary', 'adv_amount'];

/**
 * The columns that change the Salary total, and so make a hand-typed Gross
 * Salary stale. `salary` directly; `scale`, `per_hr` and `no_hrs` because
 * updateRow() recomputes salary from them. `adv_amount` is deliberately absent
 * — an advance is a deduction and never part of Gross.
 */
const GROSS_AFFECTING_FIELDS: (keyof DailyRow)[] = ['scale', 'per_hr', 'no_hrs', 'salary'];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const totalDaysInMonth = (month: number, year: number) => new Date(year, month, 0).getDate();

const buildDateLabel = (dayNum: number, month: number, year: number) =>
  `${dayNum}-${MONTH_ABBR[month - 1]}-${String(year).slice(-2)}`;

const buildDayLabel = (dayNum: number, month: number, year: number) =>
  DAY_NAMES[new Date(year, month - 1, dayNum).getDay()];

/** Build fresh default rows for the selected month/year using the staff's base salary and department. */
const buildRows = (month: number, year: number, scale: number, department: string): DailyRow[] => {
  const days  = totalDaysInMonth(month, year);
  const per_hr = scale / days / 12;
  const no_hrs = 12;
  const salary = Math.round(per_hr * no_hrs * 100) / 100;

  return Array.from({ length: days }, (_, i) => {
    const dayNum = i + 1;
    return {
      date:       buildDateLabel(dayNum, month, year),
      day:        buildDayLabel(dayNum, month, year),
      department,
      remarks:    '',
      shift_type: '24 Hrs',
      scale,
      per_hr,
      no_hrs,
      salary,
      adv_date:   '',
      tid:        '',
      adv_amount: 0,
      row_color:  null,
      remarks_color: null,
    };
  });
};

/** Restore rows fetched from the backend by rebuilding the date/day display labels. */
const restoreRows = (savedRows: any[], month: number, year: number): DailyRow[] =>
  savedRows.map((r) => ({
    date:       buildDateLabel(r.day_number, month, year),
    day:        buildDayLabel(r.day_number, month, year),
    department: r.department ?? '',
    remarks:    r.remarks    ?? '',
    shift_type: r.shift_type ?? '24 Hrs',
    scale:      r.scale,
    per_hr:     r.per_hr,
    no_hrs:     r.no_hrs,
    salary:     r.salary,
    adv_date:   r.adv_date   ?? '',
    tid:        r.tid        ?? '',
    adv_amount: r.adv_amount,
    row_color:  r.row_color  ?? null,
    remarks_color: r.remarks_color ?? null,
  }));

/**
 * Returns an updated rows array after changing one field on one row.
 *
 * Recalculation rules:
 *  - scale changes   → recalculate per_hr  (scale / days / 12), then salary
 *  - no_hrs changes  → recalculate salary  (per_hr * no_hrs)
 *  - per_hr changes  → recalculate salary  (per_hr * no_hrs)   [manual override cascade]
 *  - salary changes  → accept override, no recalc
 *  - tid typed (non-empty) + adv_date empty → auto-fill adv_date with that row's date
 */
const updateRow = (
  rows:      DailyRow[],
  index:     number,
  field:     keyof DailyRow,
  rawValue:  string,
  days:      number,
): DailyRow[] =>
  rows.map((row, i) => {
    if (i !== index) return row;

    const u: DailyRow = { ...row };

    if (NUMERIC_FIELDS.includes(field)) {
      (u as any)[field] = rawValue === '' ? 0 : (parseFloat(rawValue) || 0);
    } else {
      (u as any)[field] = rawValue;
    }

    if (field === 'scale') {
      u.per_hr = u.scale / days / 12;
      u.salary = Math.round(u.per_hr * u.no_hrs * 100) / 100;
    } else if (field === 'no_hrs' || field === 'per_hr') {
      u.salary = Math.round(u.per_hr * u.no_hrs * 100) / 100;
    }

    if (field === 'tid' && rawValue.length > 0 && !u.adv_date) {
      u.adv_date = row.date;
    }

    return u;
  });

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

// Bank fields management staff keep (a subset of what nurses have — Account
// Name / Type / State are nurse-only). Plain-text; saved on the record itself.
const MGMT_BANK_FIELDS = [
  { key: 'bank_account_number', label: 'Account Number' },
  { key: 'bank_name',           label: 'Bank' },
  { key: 'branch',              label: 'Branch' },
  { key: 'bank_ifsc_code',      label: 'IFSC' },
];

const ManagementDailySalarySheet: React.FC = () => {
  const { id }         = useParams<{ id: string }>();
  const staffId        = Number(id);
  const [searchParams] = useSearchParams();
  const navigate       = useNavigate();
  const API_BASE       = (process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000').replace(/\/$/, '');

  const initMonth = parseInt(searchParams.get('month') || String(new Date().getMonth() + 1));
  const initYear  = parseInt(searchParams.get('year')  || String(new Date().getFullYear()));

  const [selectedMonth, setSelectedMonth] = useState(initMonth);
  const [selectedYear,  setSelectedYear]  = useState(initYear);

  const [staffName,    setStaffName]    = useState('');
  const [photoUrl,     setPhotoUrl]     = useState<string | null>(null);
  const [designation,  setDesignation]  = useState('');
  const [rows,         setRows]         = useState<DailyRow[]>([]);
  // Legacy flat PT amount, still sent to /api/management-daily-salary for
  // backward compatibility with rows saved before HRA/Allowance/TDS existed
  // here (see _management_month_summary's fallback on the backend). The real
  // source of truth once a settings row exists is ptEnabled/ptValue below.
  const [pt,           setPt]           = useState(200);
  // Same shape as SalarySheet.tsx's settings (staff_type='management'), minus
  // Hostel — management staff don't have a hostel deduction. PT defaults to
  // on/200 (not off, unlike nurse) so a never-configured month keeps behaving
  // like it always did before this sheet had a PT toggle at all.
  const [hraEnabled,       setHraEnabled]       = useState<boolean>(false);
  const [hraValue,         setHraValue]         = useState<number>(0);
  const [allowanceEnabled, setAllowanceEnabled] = useState<boolean>(false);
  const [allowanceValue,   setAllowanceValue]   = useState<number>(0);
  const [ptEnabled,        setPtEnabled]        = useState<boolean>(true);
  const [ptValue,          setPtValue]          = useState<number>(200);
  const [tdsEnabled,       setTdsEnabled]       = useState<boolean>(false);
  const [tdsPercent,       setTdsPercent]       = useState<number>(0);
  const [subtotalOverride, setSubtotalOverride] = useState<number | null>(null);
  // What's in the Sub Total box while it has focus — it holds the real figure,
  // not a placeholder, so it can be edited like any other number.
  const [subtotalDraft,    setSubtotalDraft]    = useState<string | null>(null);
  // Hand-typed Gross Salary. null means "nothing typed", so the box keeps
  // tracking Sub Total + HRA + Allowance as those change; a number wins over
  // that computed figure and is what the P&L reads for this staff member.
  const [grossOverride, setGrossOverride] = useState<number | null>(null);
  // Held while the box has focus so a half-typed value is not reformatted out
  // from under the caret.
  const [grossDraft,    setGrossDraft]    = useState<string | null>(null);
  // Gross here is Sub Total + HRA + Allowance, so changing any of those makes a
  // typed figure stale exactly as editing a Salary cell does (see
  // GROSS_AFFECTING_FIELDS). One path, so every control drops it consistently.
  const clearGrossOverride = () => { setGrossOverride(null); setGrossDraft(null); };
  const [loading,      setLoading]      = useState(true);
  const [saving,       setSaving]       = useState(false);

  // Bank details — read-only until Edit, saved via PATCH /management-staff.
  const [bank,        setBank]        = useState<Record<string, string>>({});
  const [bankEditing, setBankEditing] = useState(false);
  const [bankDraft,   setBankDraft]   = useState<Record<string, string>>({});
  const [bankSaving,  setBankSaving]  = useState(false);
  const startBankEdit = () => { setBankDraft({ ...bank }); setBankEditing(true); };
  const saveBankDetails = async () => {
    setBankSaving(true);
    try {
      const fd = new FormData();
      fd.append('staff_data', JSON.stringify({
        bank_account_number: bankDraft.bank_account_number || null,
        bank_name:           bankDraft.bank_name || null,
        branch:              bankDraft.branch || null,
        bank_ifsc_code:      bankDraft.bank_ifsc_code || null,
      }));
      await axios.patch(`${API_BASE}/api/management-staff/${staffId}`, fd, { headers: authHeaders() });
      setBank({ ...bankDraft });
      setBankEditing(false);
    } catch (e) { console.error('save bank', e); alert('Failed to save bank details.'); }
    finally { setBankSaving(false); }
  };

  const [isMonthOpen, setIsMonthOpen] = useState(false);
  const [isYearOpen,  setIsYearOpen]  = useState(false);
  const monthRef = useRef<HTMLDivElement>(null);
  const yearRef  = useRef<HTMLDivElement>(null);

  // Row index whose Remarks cell opened the picker; null = picker closed.
  const [remarksRow, setRemarksRow] = useState<number | null>(null);
  // Snapshot of which rows a bulk Remarks pick should apply to, taken the
  // instant the picker opens — NOT re-read from selectedCells at pick time,
  // because clicking inside the picker (which renders outside the table)
  // trips the "click outside the table clears the selection" handler below,
  // wiping selectedCells before onSelect ever runs. null = single-row pick.
  const [remarksBulkTargets, setRemarksBulkTargets] = useState<Set<number> | null>(null);
  const openRemarksPicker = (idx: number) => {
    setRemarksRow(idx);
    setRemarksBulkTargets(selectedColumn === 'remarks' && selectedCells.size > 1 ? new Set(selectedCells) : null);
  };

  const authHeaders = useCallback(() => {
    const token = localStorage.getItem('accessToken');
    return token ? { Authorization: `Bearer ${token}` } : {};
  }, []);

  // Close dropdowns on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (monthRef.current && !monthRef.current.contains(e.target as Node)) setIsMonthOpen(false);
      if (yearRef.current  && !yearRef.current.contains(e.target as Node))  setIsYearOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Fetch staff info and saved data whenever staffId / month / year change
  useEffect(() => {
    if (!staffId) return;
    // The picker points at a row index — rows are about to be replaced.
    setRemarksRow(null);

    const fetchAll = async () => {
      setLoading(true);
      try {
        // 1. Fetch staff details
        const staffRes = await axios.get(
          `${API_BASE}/api/management-staff/${staffId}`,
          { headers: authHeaders() },
        );
        const baseScale   = staffRes.data.monthly_base_salary ?? 0;
        const baseDept    = staffRes.data.department ?? '';
        setStaffName(staffRes.data.full_name ?? '');
        setPhotoUrl(staffRes.data.photo_url ?? null);
        setDesignation(staffRes.data.designation ?? '');
        setBank({
          bank_account_number: staffRes.data.bank_account_number ?? '',
          bank_name:           staffRes.data.bank_name ?? '',
          branch:              staffRes.data.branch ?? '',
          bank_ifsc_code:      staffRes.data.bank_ifsc_code ?? '',
        });
        setBankEditing(false);

        // 2. Try to load saved daily data
        let legacyPt = 200;
        let hasSavedRows = false;
        try {
          const savedRes = await axios.get(`${API_BASE}/api/management-daily-salary`, {
            params:  { staff_id: staffId, month: selectedMonth, year: selectedYear },
            headers: authHeaders(),
          });
          if (savedRes.data.rows?.length > 0) {
            hasSavedRows = true;
            setRows(restoreRows(savedRes.data.rows, selectedMonth, selectedYear));
            legacyPt = savedRes.data.pt_amount ?? 200;
            setPt(legacyPt);
            // ?? null, not || null: a saved 0 is a real gross (a full month of
            // LOP) and must not be read back as "nothing typed".
            setGrossOverride(savedRes.data.gross_override ?? null);
            setGrossDraft(null);
          }
        } catch (err: any) {
          // Only a missing sheet is an expected miss. Anything else — notably a
          // 401 from an expired session — has to surface here: swallowing it
          // built a fresh-looking sheet that only failed once the user had
          // filled it in and pressed Save.
          if (err?.response?.status !== 404) throw err;
        }
        if (!hasSavedRows) {
          // 3. No saved data: initialize with defaults
          setRows(buildRows(selectedMonth, selectedYear, baseScale, baseDept));
          setPt(200);
        }

        // 4. HRA/Allowance/PT/TDS settings — same shared table & endpoint the
        // nurse sheet uses (staff_type='management'), minus Hostel. If no
        // settings row has ever been saved for this staff/month, fall back to
        // the legacy flat PT (on, at whatever amount the old sheet had) rather
        // than the endpoint's nurse-oriented defaults (PT off, TDS on at 10%,
        // hostel on) — so a month nobody has touched yet still looks exactly
        // like it always did.
        try {
          const setRes = await axios.get(`${API_BASE}/api/salary-settings`, {
            params:  { staff_id: staffId, staff_type: 'management', month: selectedMonth, year: selectedYear },
            headers: authHeaders(),
          });
          const s = setRes.data;
          const hasSettingsRow = !!s && s.id !== 0;
          setHraEnabled(s?.hra_enabled ?? false);       setHraValue(s?.hra_amount ?? 0);
          setAllowanceEnabled(s?.allowance_enabled ?? false); setAllowanceValue(s?.allowance_amount ?? 0);
          setPtEnabled(hasSettingsRow ? (s.pt_enabled ?? false) : true);
          setPtValue(hasSettingsRow ? (s.pt_amount ?? 0) : legacyPt);
          setTdsEnabled(hasSettingsRow ? (s.tds_enabled ?? false) : false);
          setTdsPercent(hasSettingsRow ? (s.tds_percent ?? 0) : 0);
          setSubtotalOverride(s?.subtotal_override ?? null);
        } catch (err) {
          console.error('Failed to load salary settings', err);
        }
      } catch (err: any) {
        // The staff lookup is unauthenticated, so a 401 can only come from the
        // sheet fetch — say so plainly instead of blaming the staff record.
        toast.error(
          err?.response?.status === 401
            ? 'Session expired — please log in again.'
            : 'Failed to load staff data.',
        );
      } finally {
        setLoading(false);
      }
    };

    fetchAll();
  }, [staffId, selectedMonth, selectedYear]); // eslint-disable-line react-hooks/exhaustive-deps

  // -------------------------------------------------------------------------
  // Save
  // -------------------------------------------------------------------------
  const handleSave = async () => {
    if (rows.length === 0) return;
    setSaving(true);
    try {
      await Promise.all([
        axios.post(
          `${API_BASE}/api/management-daily-salary`,
          {
            staff_id:  staffId,
            month:     selectedMonth,
            year:      selectedYear,
            // Kept in sync for backward compatibility — see the state comment
            // on `pt` above. The real source of truth is the settings row below.
            pt_amount: ptAmount,
            gross_override: grossOverride,
            rows: rows.map((r, i) => ({
              day_number: i + 1,
              department: r.department,
              remarks:    r.remarks,
              shift_type: r.shift_type,
              scale:      r.scale,
              per_hr:     r.per_hr,
              no_hrs:     r.no_hrs,
              salary:     r.salary,
              adv_date:   r.adv_date,
              tid:        r.tid,
              adv_amount: r.adv_amount,
              row_color:  r.row_color,
              remarks_color: r.remarks_color,
            })),
          },
          { headers: authHeaders() },
        ),
        // Same shared settings table & endpoint the nurse sheet uses, staff_type
        // 'management' — hostel is always off/0 here since management staff
        // don't have that deduction.
        axios.post(
          `${API_BASE}/api/salary-settings`,
          {
            staff_id: staffId, staff_type: 'management', month: selectedMonth, year: selectedYear,
            hra_enabled: hraEnabled, hra_amount: hraValue,
            allowance_enabled: allowanceEnabled, allowance_amount: allowanceValue,
            hostel_enabled: false, hostel_amount: 0,
            pt_enabled: ptEnabled, pt_amount: ptValue,
            tds_enabled: tdsEnabled, tds_percent: tdsPercent,
            subtotal_override: subtotalOverride,
            net_salary: Math.round(netTakeHome),
          },
          { headers: authHeaders() },
        ),
      ]);
      setSelectedRows(new Set());
      toast.success('Changes saved successfully');
    } catch (err: any) {
      toast.error(
        err?.response?.status === 401
          ? 'Session expired — please log in again. Your entries are still on screen.'
          // 400 carries a sentence meant for the user (e.g. a resigned staff
          // member's month); anything else is not safe to show as-is.
          : err?.response?.status === 400 && typeof err.response.data?.detail === 'string'
            ? err.response.data.detail
            : 'Failed to save salary sheet.',
      );
    } finally {
      setSaving(false);
    }
  };

  // -------------------------------------------------------------------------
  // Cell change
  // -------------------------------------------------------------------------
  // Enter commits a fill: drop the selection so the new values are readable
  // against normal striping, and say that Save is still required — nothing here
  // writes to the server, and a filled grid looks saved when it isn't.
  const commitSelection = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== 'Enter') return;
    const filled = selectedCells.size > 1 || selectedRows.size > 1;
    e.preventDefault();
    (e.target as HTMLInputElement).blur();
    clearColumnSel();
    setSelectedRows(new Set());
    if (filled) {
      toast('Values updated — click Save Changes to keep them.', { icon: '📝' });
    }
  };

  const handleChange = (index: number, field: keyof DailyRow, value: string) => {
    const days = totalDaysInMonth(selectedMonth, selectedYear);

    // A typed Gross Salary describes the day rows as they stood when it was
    // entered. Touching any column that feeds the Salary total makes it stale,
    // so the box goes back to tracking the computed sum rather than quietly
    // reporting a figure the rows no longer add up to. Adv. Amount is exempt:
    // advances are a deduction and never part of Gross.
    if (GROSS_AFFECTING_FIELDS.includes(field)) {
      setGrossOverride(null);
      setGrossDraft(null);
    }

    // Spreadsheet-style fill: editing a number column on a row that is part of a
    // multi-row selection applies the value to every selected row. Text columns
    // (Department, Remarks, Shift Type, Adv Date, TID) stay per-row — they are
    // rarely identical across a range and are easy to overwrite by accident.
    const spread = NUMERIC_FIELDS.includes(field) && selectedRows.has(index) && selectedRows.size > 1;
    // Column range fill: this cell is in the selected column range → set every selected cell.
    const fillCol = selectedColumn === field && selectedCells.has(index);

    setRows((prev) => {
      if (fillCol) {
        let next = prev;
        selectedCells.forEach((i) => { next = updateRow(next, i, field, value, days); });
        return next;
      }
      if (!spread) return updateRow(prev, index, field, value, days);
      let next = prev;
      // updateRow re-derives per_hr/salary per row, so each one recalculates off
      // its own values rather than inheriting the edited row's results.
      selectedRows.forEach((i) => { next = updateRow(next, i, field, value, days); });
      return next;
    });
  };

  // -------------------------------------------------------------------------
  // Drag-select + bulk fill — same interaction as the nurse/physio Salary Sheet:
  // drag across rows to select them, then typing into any number column of a
  // selected row writes that value to every selected row.
  // -------------------------------------------------------------------------
  const [selectedRows, setSelectedRows] = useState<Set<number>>(new Set());
  // Which rows the highlight-color toolbar should act on: the rows just
  // bulk-filled with a Remarks value take priority (so picking "Leave" for a
  // range and then a color feels like one continuous action), falling back to
  // a plain whole-row drag-selection when there's no recent Remarks pick.
  const colorTargets: Set<number> | null = remarksBulkTargets ?? (selectedRows.size >= 1 ? selectedRows : null);
  // Freeform row highlight — applied to every row in colorTargets at once.
  // Deliberately does NOT clear the selection afterward, so the toolbar stays
  // open — picking a highlight then a text color for the same rows is one
  // continuous action instead of having to re-select in between.
  const applyRowColor = (color: string | null) => {
    if (!colorTargets) return;
    setRows((prev) => prev.map((r, i) => (colorTargets.has(i) ? { ...r, row_color: color } : r)));
  };
  // Font color for the Remarks text — same targeting, independent field.
  const applyRemarksColor = (color: string | null) => {
    if (!colorTargets) return;
    setRows((prev) => prev.map((r, i) => (colorTargets.has(i) ? { ...r, remarks_color: color } : r)));
  };
  const dragging     = useRef(false);                 // a drag-select is in progress
  const dragAnchor   = useRef<number | null>(null);   // row the drag started on
  const dragAdditive = useRef<Set<number>>(new Set()); // prior selection, for Ctrl-drag
  const pendingFromControl = useRef(false);            // pressed inside an input — armed, not yet dragging
  const tableRef = useRef<HTMLTableElement>(null);      // for click-outside deselect
  // The floating selection toolbar (row-count toast + color swatches) renders
  // outside the table, so a click on it would otherwise register as "outside
  // the table" and clear the selection it's meant to act on.
  const toolbarRef = useRef<HTMLDivElement>(null);

  // End any drag when the mouse is released anywhere on the page.
  useEffect(() => {
    const up = () => { dragging.current = false; dragAnchor.current = null; pendingFromControl.current = false; };
    window.addEventListener('mouseup', up);
    return () => window.removeEventListener('mouseup', up);
  }, []);

  // Begin a drag-select on a row — from anywhere in it, inputs included.
  //
  // Pressing on an input only *arms* the drag: the selection is left untouched
  // and the click is allowed through, so the field focuses normally and a range
  // you already selected survives while you go type into one of its cells. The
  // drag only takes over once the pointer actually reaches a different row.
  const startDrag = (idx: number, e: React.MouseEvent) => {
    clearColumnSel(); // a whole-row drag isn't a column-range selection
    setRemarksBulkTargets(null); // starting a genuinely new selection — drop any leftover Remarks-fill snapshot
    dragging.current = true;
    dragAnchor.current = idx;
    dragAdditive.current = (e.ctrlKey || e.metaKey) ? new Set(selectedRows) : new Set();

    const tag = (e.target as HTMLElement).tagName.toUpperCase();
    if (['INPUT', 'SELECT', 'OPTION', 'BUTTON', 'TEXTAREA', 'SVG', 'PATH'].includes(tag)) {
      pendingFromControl.current = true;
      return;
    }

    pendingFromControl.current = false;
    e.preventDefault(); // suppress text selection during the drag
    const base = new Set(dragAdditive.current);
    base.add(idx);
    setSelectedRows(base);
  };


  // Deselect when the click lands outside the grid. Cheap to attach because it
  // only exists while something is actually selected.
  useEffect(() => {
    if (selectedRows.size === 0) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (tableRef.current && !tableRef.current.contains(t) && !toolbarRef.current?.contains(t)) {
        setSelectedRows(new Set());
      }
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [selectedRows.size]);

  // Extend the selection to the row currently under the cursor while dragging.
  const extendDrag = (idx: number) => {
    if (!dragging.current || dragAnchor.current == null) return;

    if (pendingFromControl.current) {
      if (idx === dragAnchor.current) return; // still on the row we pressed in
      // A real drag has begun. Drop focus so the input stops extending its own
      // text selection, and clear any stray range the press already started.
      pendingFromControl.current = false;
      (document.activeElement as HTMLElement | null)?.blur();
    }
    window.getSelection()?.removeAllRanges();

    const lo = Math.min(dragAnchor.current, idx);
    const hi = Math.max(dragAnchor.current, idx);
    const base = new Set(dragAdditive.current);
    for (let i = lo; i <= hi; i++) base.add(i);
    setSelectedRows(base);
  };

  // A stale selection would fill the wrong days once the grid reloads.
  useEffect(() => { setSelectedRows(new Set()); }, [selectedMonth, selectedYear, staffId]);

  // ── Column range fill — drag within a column to select a range of cells, then
  //    type to fill it (Esc / click-outside clears). Cell key = row index. ──────
  const [selectedColumn, setSelectedColumn] = useState<keyof DailyRow | null>(null);
  const [selectedCells, setSelectedCells] = useState<Set<number>>(new Set());
  const cellDrag = useRef<{ field: keyof DailyRow; anchorIdx: number } | null>(null);
  // The row the cell-drag last reached, so mouseup knows where to put the caret.
  const lastCellHover = useRef<number | null>(null);

  // ── Typing straight into a fresh selection ──────────────────────────────────
  // Every cell input registers itself here under `${field}:${rowIndex}` so a
  // selection can hand the caret to its last cell without the user clicking
  // again. Refs rather than state: focusing is a side effect on a DOM node and
  // must not re-render the grid on every mount.
  const cellInputs = useRef<Map<string, HTMLInputElement>>(new Map());
  const cellKey = (field: keyof DailyRow, idx: number) => `${String(field)}:${idx}`;
  const registerCell = (field: keyof DailyRow, idx: number) => (el: HTMLInputElement | null) => {
    const k = cellKey(field, idx);
    if (el) cellInputs.current.set(k, el);
    else cellInputs.current.delete(k);
  };
  // Focus the anchor cell of a selection and preselect its text, so the first
  // keystroke replaces the value instead of appending to it. Deferred a frame:
  // the drag's own blur() (see extendDrag/extendCellSel) would otherwise land
  // after this and take the caret straight back out.
  const focusCell = (field: keyof DailyRow, idx: number) => {
    requestAnimationFrame(() => {
      const el = cellInputs.current.get(cellKey(field, idx));
      if (!el) return;
      el.focus();
      el.select();
    });
  };
    const FILL_COLUMN_LABELS: Record<string, string> = { department: 'Department', remarks: 'Remarks', scale: 'Scale', per_hr: 'Per Hr', no_hrs: 'No. Hrs', salary: 'Salary', shift_type: 'Shift Type', adv_date: 'Adv. Date', tid: 'TID', adv_amount: 'Amount' };
    // Columns a selected range can be summed over, shown below Bank Details.
    const SUMMABLE_FIELDS: (keyof DailyRow)[] = ['per_hr', 'no_hrs', 'salary', 'adv_amount'];
    const selectedColumnSum = (selectedColumn && selectedCells.size > 0 && SUMMABLE_FIELDS.includes(selectedColumn))
      ? Array.from(selectedCells).reduce((sum, i) => sum + (Number(rows[i]?.[selectedColumn as keyof DailyRow]) || 0), 0)
      : null;
  // Deliberately does NOT clear remarksBulkTargets — this runs on every
  // "click outside the table" too, and the Remarks picker popup renders
  // outside the table, so clicking inside it to make a pick would otherwise
  // wipe the snapshot a moment before onSelect gets to use it.
  const clearColumnSel = () => { setSelectedColumn(null); setSelectedCells(new Set()); };
  const pickColumn = (field: keyof DailyRow) => {
    if (selectedColumn === field) { clearColumnSel(); return; }
    setSelectedRows(new Set());
    setSelectedColumn(field);
    setSelectedCells(new Set(rows.map((_, i) => i)));
    // Whole column picked — put the caret in the last cell, ready to type.
    if (rows.length > 0) focusCell(field, rows.length - 1);
  };
  const startCellSel = (field: keyof DailyRow, idx: number, e: React.MouseEvent) => {
    e.stopPropagation();  // don't also trigger the whole-row drag
    cellDrag.current = { field, anchorIdx: idx };
    lastCellHover.current = idx;
    setSelectedRows(new Set());
    // Starting a selection on a different column drops any leftover Remarks
    // bulk-pick snapshot — it's re-taken fresh from openRemarksPicker anyway,
    // so this only matters for not offering a stale color target elsewhere.
    if (field !== 'remarks') setRemarksBulkTargets(null);
    if (!(selectedColumn === field && selectedCells.has(idx))) { setSelectedColumn(field); setSelectedCells(new Set([idx])); }
  };
  const extendCellSel = (field: keyof DailyRow, idx: number) => {
    const cd = cellDrag.current;
    if (!cd || cd.field !== field || idx === cd.anchorIdx) return;
    lastCellHover.current = idx;
    (document.activeElement as HTMLElement | null)?.blur();
    window.getSelection()?.removeAllRanges();
    const lo = Math.min(cd.anchorIdx, idx), hi = Math.max(cd.anchorIdx, idx);
    const set = new Set<number>();
    for (let i = lo; i <= hi; i++) set.add(i);
    setSelectedColumn(field);
    setSelectedCells(set);
  };
  useEffect(() => {
    // Escape is a deliberate, keyboard-driven dismissal — unlike a click inside
    // the Remarks picker, it can never be mistaken for "finish my bulk pick",
    // so it's safe to also drop the color-toolbar snapshot here.
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') { clearColumnSel(); setRemarksBulkTargets(null); } };
    const onUp = () => {
      // Releasing the drag leaves the range selected; hand the caret to the
      // cell the cursor stopped on, so typing can start immediately.
      const cd = cellDrag.current;
      cellDrag.current = null;
      if (cd && lastCellHover.current !== null) focusCell(cd.field, lastCellHover.current);
    };
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (tableRef.current && !tableRef.current.contains(t) && !toolbarRef.current?.contains(t)) clearColumnSel();
    };
    window.addEventListener('keydown', onKey);
    window.addEventListener('mouseup', onUp);
    document.addEventListener('mousedown', onDown);
    return () => { window.removeEventListener('keydown', onKey); window.removeEventListener('mouseup', onUp); document.removeEventListener('mousedown', onDown); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  // Switching month/year/staff rebuilds `rows` from scratch — any leftover
  // snapshot would point at day-indices from the sheet that's about to
  // disappear, so it must not survive the switch.
  useEffect(() => { clearColumnSel(); setRemarksBulkTargets(null); }, [selectedMonth, selectedYear, staffId]); // eslint-disable-line react-hooks/exhaustive-deps

  // -------------------------------------------------------------------------
  // Computed totals
  // -------------------------------------------------------------------------
  const salarySum       = rows.reduce((s, r) => s + r.salary,     0);
  const totalAdvances   = rows.reduce((s, r) => s + r.adv_amount, 0);
  // Sub Total — the sum of the Salary column, unless typed over — same pattern
  // as SalarySheet.tsx. Gross = Sub Total + HRA + Allowance (no Hostel term:
  // management staff don't have one).
  const subTotal        = subtotalOverride ?? salarySum;
  const hraAmount       = hraEnabled ? hraValue : 0;
  const allowanceAmount = allowanceEnabled ? allowanceValue : 0;
  const computedGross   = subTotal + hraAmount + allowanceAmount;
  // A typed figure wins. Net Take Home, the header stats, both exports and the
  // P&L all read grossSalary, so they follow whichever is in force without
  // needing to know which it is.
  const grossSalary     = grossOverride ?? computedGross;
  const ptAmount        = ptEnabled ? ptValue : 0;
  const tdsAmount       = tdsEnabled && tdsPercent > 0 ? (grossSalary * tdsPercent) / 100 : 0;
  const totalDeductions = ptAmount + tdsAmount + totalAdvances;
  const netTakeHome     = grossSalary - totalDeductions;

  const fmt = (n: number) =>
    n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  // Gross Salary and Net Take Home only — rounded to a whole rupee, no paise.
  // Everything else fmt() formats (TDS, PT, Advances, Total Deductions) keeps
  // its 2-decimal precision.
  const fmtWhole = (n: number) => Math.round(n).toLocaleString('en-IN');

  const monthLabel = MONTHS.find((m) => m.val === selectedMonth)?.label ?? '';

  // -------------------------------------------------------------------------
  // Export
  // -------------------------------------------------------------------------
  const handleExportCSV = () => {
    const headers = ['Date','Day','Department','Remarks','Shift Type','Scale','Per Hr','No. Hrs','Salary','Adv. Date','TID','Adv. Amt'];
    const data = rows.map((r) => [
      r.date, r.day, `"${r.department}"`, `"${r.remarks}"`, r.shift_type,
      r.scale, r.per_hr.toFixed(6), r.no_hrs, r.salary,
      r.adv_date || '', r.tid || '', r.adv_amount || '',
    ]);
    const csv = [headers, ...data].map((row) => row.join(',')).join('\n');
    saveAs(new Blob([csv], { type: 'text/csv;charset=utf-8;' }),
      `${staffName} - Daily Salary - ${monthLabel} ${selectedYear}.csv`);
  };

  const handleExportExcel = async () => {
    const wb  = new ExcelJS.Workbook();
    const ws  = wb.addWorksheet('Daily Salary');
    ws.columns = [
      { header: 'Date',       key: 'date',    width: 12 },
      { header: 'Day',        key: 'day',     width: 12 },
      { header: 'Department', key: 'dept',    width: 18 },
      { header: 'Remarks',    key: 'remarks', width: 20 },
      { header: 'Shift Type', key: 'shift',   width: 12 },
      { header: 'Scale',      key: 'scale',   width: 12 },
      { header: 'Per Hr',     key: 'per_hr',  width: 14 },
      { header: 'No. Hrs',    key: 'no_hrs',  width: 10 },
      { header: 'Salary',     key: 'salary',  width: 12 },
      { header: 'Date',  key: 'adv_date',width: 12 },
      { header: 'TID',        key: 'tid',     width: 18 },
      { header: 'Amount',   key: 'adv_amt', width: 12 },
    ];
    ws.spliceRows(1, 0, [], []);
    ws.mergeCells(1, 1, 1, 12);
    const titleRow = ws.getRow(1);
    titleRow.height = 28;
    titleRow.getCell(1).value     = `${staffName} — Daily Salary Sheet — ${monthLabel} ${selectedYear}`;
    titleRow.getCell(1).font      = { size: 14, bold: true };
    titleRow.getCell(1).alignment = { horizontal: 'center', vertical: 'middle' };

    const hdr = ws.getRow(3);
    hdr.height = 22;
    hdr.eachCell((cell) => {
      cell.font      = { bold: true, color: { argb: 'FFFFFFFF' } };
      cell.fill      = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1E3A8A' } };
      cell.alignment = { horizontal: 'center', vertical: 'middle' };
      cell.border    = { top:{style:'thin'}, left:{style:'thin'}, bottom:{style:'thin'}, right:{style:'thin'} };
    });

    rows.forEach((r) => {
      const row = ws.addRow([
        r.date, r.day, r.department, r.remarks, r.shift_type,
        r.scale, parseFloat(r.per_hr.toFixed(6)), r.no_hrs, r.salary,
        r.adv_date || '', r.tid || '', r.adv_amount || '',
      ]);
      row.eachCell((cell) => {
        cell.alignment = { horizontal: 'center', vertical: 'middle' };
        cell.border = { top:{style:'thin'}, left:{style:'thin'}, bottom:{style:'thin'}, right:{style:'thin'} };
      });
      if (r.day === 'Sunday') row.getCell(2).font = { color: { argb: 'FFDC2626' }, bold: true };
    });

    const addFooter = (label: string, val: number, bg?: string, red = false) => {
      const fr = ws.addRow([label,'','','','','','','',val]);
      ws.mergeCells(`A${fr.number}:H${fr.number}`);
      fr.getCell(1).alignment = { horizontal: 'right' };
      fr.getCell(1).font = { bold: true };
      fr.getCell(9).font = { bold: true, color: red ? { argb: 'FF991B1B' } : undefined };
      if (bg) fr.getCell(9).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: bg } };
      fr.eachCell((cell) => {
        cell.border = { top:{style:'thin'}, left:{style:'thin'}, bottom:{style:'thin'}, right:{style:'thin'} };
      });
    };
    addFooter('Sub Total', parseFloat(subTotal.toFixed(2)));
    addFooter(`HRA (${hraEnabled ? 'Yes' : 'No'})`, hraAmount);
    addFooter(`Allowance (${allowanceEnabled ? 'Yes' : 'No'})`, allowanceAmount);
    addFooter('Gross Salary',    Math.round(grossSalary),    'FFBFDBFE');
    addFooter(`Professional Tax (${ptEnabled ? 'Yes' : 'No'})`, ptAmount, undefined, true);
    addFooter(`TDS (${tdsEnabled ? tdsPercent + '%' : 'No'})`, parseFloat(tdsAmount.toFixed(2)), undefined, true);
    addFooter('Advances',  parseFloat(totalAdvances.toFixed(2)),  undefined, true);
    addFooter('Total Deductions',parseFloat(totalDeductions.toFixed(2)),undefined, true);
    addFooter('Net Take Home',   Math.round(netTakeHome),    'FF86EFAC');

    const buf = await wb.xlsx.writeBuffer();
    saveAs(new Blob([buf]), `${staffName} - Daily Salary - ${monthLabel} ${selectedYear}.xlsx`);
  };

  // -------------------------------------------------------------------------
  // Styles
  // -------------------------------------------------------------------------
  // ---------------------------------------------------------------------------
  // Styles — values copied verbatim from SalarySheet.tsx (styles.*).
  // Management-only extras: salaryTh, salaryTd, deductionHeader.
  // ---------------------------------------------------------------------------
  const S: { [k: string]: React.CSSProperties } = {
    page:    { padding: '1rem 1.25rem', backgroundColor: '#f8fafc', minHeight: '100vh', fontFamily: '"Inter", sans-serif' },
    header:  { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem', gap: '1rem', flexWrap: 'nowrap' as const },
    title:   { fontSize: '1.15rem', fontWeight: 700, color: '#0f172a', margin: 0, lineHeight: 1.2 },
    subtitle:{ color: '#64748b', fontSize: '0.72rem', marginTop: '0.1rem', textTransform: 'capitalize' as const },
    backBtn: { display: 'flex', alignItems: 'center', gap: '0.5rem', border: 'none', background: 'transparent', color: '#64748b', cursor: 'pointer', marginBottom: '1rem', fontSize: '0.9rem', fontWeight: 500 },

    // Controls group — white pill wrapping all right-side controls (matches SalarySheet controlsGroup)
    controlsGroup:       { display: 'flex', gap: '0.5rem', alignItems: 'center', backgroundColor: 'white', padding: '0.3rem', borderRadius: '10px', border: '1px solid #e2e8f0', boxShadow: '0 1px 2px rgba(0,0,0,0.05)', flexShrink: 0 },
    datePickerContainer: { display: 'flex', alignItems: 'center', backgroundColor: '#f1f5f9', borderRadius: '8px', padding: '0.2rem', marginRight: '0.5rem' },
    pickerBtn:  { display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.4rem 0.8rem', backgroundColor: 'transparent', border: 'none', fontSize: '0.85rem', fontWeight: 600, color: '#334155', cursor: 'pointer', borderRadius: '6px', outline: 'none', minWidth: '100px', justifyContent: 'space-between' },
    divider:    { width: '1px', height: '16px', backgroundColor: '#cbd5e1', margin: '0 2px' },
    dropdown:   { position: 'absolute', top: 'calc(100% + 8px)', right: 0, backgroundColor: 'white', border: '1px solid #e2e8f0', borderRadius: '10px', boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)', width: '150px', maxHeight: '300px', overflowY: 'auto', zIndex: 50, padding: '0.3rem' },
    dropItem:   { padding: '0.5rem 0.7rem', fontSize: '0.9rem', color: '#475569', cursor: 'pointer', borderRadius: '6px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
    dropActive: { backgroundColor: '#eff6ff', color: '#2563eb', fontWeight: 600 },
    saveBtn:  { backgroundColor: '#1e3a8a', color: 'white', border: 'none', padding: '0.6rem 1.2rem', borderRadius: '8px', fontSize: '0.9rem', fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.5rem', boxShadow: '0 2px 4px rgba(0,0,0,0.1)', marginLeft: '0.5rem' },
    csvBtn:   { backgroundColor: '#64748b', color: 'white', border: 'none', padding: '0.6rem 1rem', borderRadius: '8px', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.5rem', boxShadow: '0 1px 2px rgba(0,0,0,0.1)' },
    excelBtn: { backgroundColor: '#10b981', color: 'white', border: 'none', padding: '0.6rem 1rem', borderRadius: '8px', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.5rem', boxShadow: '0 1px 2px rgba(0,0,0,0.1)' },

    // Table chrome — identical to SalarySheet tableCard / tableWrapper / table / thead / th / td / input
    card:    { backgroundColor: 'white', borderRadius: '8px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)', border: '1px solid #cbd5e1', overflow: 'hidden' },
    wrapper: { overflowX: 'auto' },
    table:   { width: '100%', borderCollapse: 'collapse', fontSize: '0.75rem', textAlign: 'center' as const, color: '#334155' },
    thead:   { backgroundColor: '#1e3a8a', borderBottom: '1px solid #172554', position: 'sticky' as const, top: 0, zIndex: 10 },
    th:      { padding: '0.35rem 0.35rem', color: 'white', fontWeight: 600, textTransform: 'uppercase' as const, border: '1px solid #475569', whiteSpace: 'nowrap' as const, fontSize: '0.7rem' },
    td:      { padding: '0', border: '1px solid #cbd5e1', verticalAlign: 'middle' as const, height: '27px' },
    input:   { width: '100%', height: '100%', border: 'none', textAlign: 'center' as const, background: 'transparent', outline: 'none', fontSize: '0.75rem', color: '#0f172a', padding: '0 4px', fontFamily: 'inherit' },

    subtotalRow:   { backgroundColor: '#fcd34d', fontWeight: 700 },

    // Management-only: deduction header tint + salary anchor column
    deductionHeader: { backgroundColor: '#fef2f2', color: '#991b1b', fontWeight: 700, borderBottom: '1px solid #fca5a5' },
    salaryTh: { backgroundColor: '#162e6b' },
    salaryTd: { backgroundColor: '#f8fafc' },

    // Sheet + summary panel side by side, so every day row fits without the page
    // scrolling past the totals (was a stack of full-width footer rows below the
    // table, plus large stat cards above it). Mirrors SalarySheet.
    sheetRow:     { display: 'flex', alignItems: 'stretch' },
    summaryPanel: { width: '260px', flexShrink: 0, borderLeft: '2px solid #cbd5e1', display: 'flex', flexDirection: 'column' as const, backgroundColor: '#f8fafc' },
    sumRow:       { display: 'flex', alignItems: 'center', gap: '6px', padding: '0 10px', height: '32px', borderBottom: '1px solid #e2e8f0', fontSize: '0.78rem', whiteSpace: 'nowrap' as const },
    sumLabel:     { fontWeight: 600, color: '#475569', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' },
    sumInput:     { width: '58px', border: '1px solid #cbd5e1', borderRadius: '4px', textAlign: 'right' as const, fontWeight: 700, color: '#1e3a8a', outline: 'none', fontSize: '0.78rem', padding: '2px 4px' },
    sumVal:       { fontWeight: 700, color: '#1e3a8a', minWidth: '60px', textAlign: 'right' as const },
    sumSelect:    { border: '1px solid #cbd5e1', borderRadius: '4px', padding: '1px 2px', fontSize: '0.7rem', fontWeight: 600, color: '#0f172a', cursor: 'pointer', outline: 'none' },

    // Compact totals beside the name, replacing the old full-width stat cards.
    headStat:      { display: 'flex', flexDirection: 'column' as const, alignItems: 'flex-start', padding: '0.28rem 0.5rem', borderRadius: '8px', backgroundColor: 'white', border: '1px solid #e2e8f0', boxShadow: '0 1px 2px rgba(0,0,0,0.04)', minWidth: '78px', whiteSpace: 'nowrap' as const },
    headStatLabel: { fontSize: '0.58rem', color: '#64748b', fontWeight: 600, textTransform: 'uppercase' as const, letterSpacing: '0.03em' },
    headStatValue: { fontSize: '0.9rem', fontWeight: 700, lineHeight: 1.2 },
    bankHeader:     { display: 'flex', alignItems: 'center', gap: '6px', marginTop: '18px', padding: '5px 6px 5px 10px', minHeight: '26px', fontSize: '0.66rem', fontWeight: 700, textTransform: 'uppercase' as const, letterSpacing: '0.04em', color: '#475569', backgroundColor: '#e2e8f0', borderTop: '2px solid #cbd5e1', borderBottom: '1px solid #cbd5e1' },
    bankBtn:        { border: '1px solid #cbd5e1', background: '#fff', color: '#475569', borderRadius: '4px', padding: '1px 7px', fontSize: '0.64rem', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', textTransform: 'uppercase' as const, letterSpacing: '0.03em' },
    bankBtnPrimary: { border: 'none', background: '#1e3a8a', color: '#fff', borderRadius: '4px', padding: '2px 8px', fontSize: '0.64rem', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', textTransform: 'uppercase' as const, letterSpacing: '0.03em' },
    bankInput:      { width: '100%', border: '1px solid #cbd5e1', borderRadius: '4px', padding: '2px 5px', fontSize: '0.76rem', fontWeight: 600, color: '#0f172a', outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box' as const },
    bankRow:        { display: 'flex', flexDirection: 'column' as const, padding: '4px 10px', borderBottom: '1px solid #f1f5f9', backgroundColor: '#ffffff' },
    bankLabel:      { fontSize: '0.6rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase' as const, letterSpacing: '0.03em', lineHeight: 1.4 },
    bankValue:      { fontSize: '0.78rem', fontWeight: 600, color: '#0f172a', wordBreak: 'break-word' as const, minHeight: '16px' },
  };

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------
  return (
    <div style={S.page}>

      {/* ---- Header ---- */}
      <header style={S.header}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'nowrap', flex: '1 1 auto', minWidth: 0 }}>
          <button style={{ ...S.backBtn, marginBottom: 0, flexShrink: 0 }} onClick={() => navigate('/salarysheet')}>
            <ArrowLeft size={18} /> Back to Salary Sheets
          </button>
          <div style={{ minWidth: 0, flexShrink: 1, overflow: 'hidden' }}>
            <h1 style={{ ...S.title, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {loading ? 'Loading…' : staffName || 'Management Staff'}
            </h1>
            <p style={{ ...S.subtitle, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {designation || 'Management'} • Salary Sheet for {monthLabel} {selectedYear}
            </p>
          </div>
          {/* Compact totals next to the name — single row, never wraps down. */}
          <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'nowrap', flexShrink: 0 }}>
            <div style={S.headStat}>
              <span style={S.headStatLabel}>Gross Salary</span>
              <span style={{ ...S.headStatValue, color: '#0f172a' }}>₹ {fmtWhole(grossSalary)}</span>
            </div>
            <div style={S.headStat}>
              <span style={S.headStatLabel}>TDS</span>
              <span style={{ ...S.headStatValue, color: '#dc2626' }}>₹ {fmt(tdsAmount)}</span>
            </div>
            <div style={S.headStat}>
              <span style={S.headStatLabel}>P.T</span>
              <span style={{ ...S.headStatValue, color: '#dc2626' }}>₹ {fmt(ptAmount)}</span>
            </div>
            <div style={S.headStat}>
              <span style={S.headStatLabel}>Advances</span>
              <span style={{ ...S.headStatValue, color: '#ea580c' }}>₹ {fmt(totalAdvances)}</span>
            </div>
            <div style={S.headStat}>
              <span style={S.headStatLabel}>Total Deduction</span>
              <span style={{ ...S.headStatValue, color: '#991b1b' }}>₹ {fmt(totalDeductions)}</span>
            </div>
            <div style={S.headStat}>
              <span style={S.headStatLabel}>Net Salary</span>
              <span style={{ ...S.headStatValue, color: '#16a34a' }}>₹ {fmtWhole(netTakeHome)}</span>
            </div>
          </div>
        </div>

        {/* Controls pill — matches SalarySheet controlsGroup */}
        <div style={S.controlsGroup}>
          <div style={S.datePickerContainer}>
            <div style={{ position: 'relative' }} ref={monthRef}>
              <button
                style={S.pickerBtn}
                onClick={() => { setIsMonthOpen((o) => !o); setIsYearOpen(false); }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <Calendar size={15} color="#3b82f6" /><span>{monthLabel}</span>
                </div>
                <ChevronDown size={14} color="#94a3b8" />
              </button>
              {isMonthOpen && (
                <div style={S.dropdown}>
                  {MONTHS.map((m) => (
                    <div
                      key={m.val}
                      style={{ ...S.dropItem, ...(m.val === selectedMonth ? S.dropActive : {}) }}
                      onClick={() => { setSelectedMonth(m.val); setIsMonthOpen(false); }}
                    >
                      {m.label}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div style={S.divider} />
            <div style={{ position: 'relative' }} ref={yearRef}>
              <button
                style={{ ...S.pickerBtn, minWidth: '80px' }}
                onClick={() => { setIsYearOpen((o) => !o); setIsMonthOpen(false); }}
              >
                <span>{selectedYear}</span>
                <ChevronDown size={14} color="#94a3b8" />
              </button>
              {isYearOpen && (
                <div style={{ ...S.dropdown, width: '100px' }}>
                  {YEARS.map((y) => (
                    <div
                      key={y}
                      style={{ ...S.dropItem, ...(y === selectedYear ? S.dropActive : {}) }}
                      onClick={() => { setSelectedYear(y); setIsYearOpen(false); }}
                    >
                      {y}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          <button style={S.csvBtn} onClick={handleExportCSV} disabled={loading || rows.length === 0}>
            <Download size={16} /> CSV
          </button>
          <button style={S.excelBtn} onClick={handleExportExcel} disabled={loading || rows.length === 0}>
            <FileText size={16} /> Excel
          </button>
          <button style={S.saveBtn} onClick={handleSave} disabled={saving || loading || rows.length === 0}>
            {saving ? <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} /> : <Save size={16} />} Save Changes
          </button>
        </div>
      </header>

      {/* ---- Grid ---- */}
      <div style={S.card}>
        {loading ? (
          <div style={{ padding: '4rem', textAlign: 'center', color: '#94a3b8' }}>
            <Loader2 size={28} style={{ animation: 'spin 1s linear infinite', marginBottom: '0.5rem' }} />
            <div>Loading salary data…</div>
          </div>
        ) : (
          <div style={S.sheetRow}>
            <div style={{ ...S.wrapper, flex: 1, minWidth: 0 }}>
            <table ref={tableRef} style={S.table}>
              <thead style={S.thead}>
                <tr>
                  <th style={{ ...S.th, minWidth: '75px'  }}>Date</th>
                  <th style={{ ...S.th, minWidth: '90px'  }}>Day</th>
                  <th onClick={() => pickColumn('department')} title="Click to select the whole column" style={{ ...S.th, minWidth: '110px', cursor: 'pointer', ...(selectedColumn === 'department' ? { outline: '3px solid #2563eb', outlineOffset: '-3px' } : {}) }}>Department</th>
                  <th onClick={() => pickColumn('remarks')} title="Click to select the whole column, then click any cell in it to pick one remark for all of them" style={{ ...S.th, minWidth: '140px', cursor: 'pointer', ...(selectedColumn === 'remarks' ? { outline: '3px solid #2563eb', outlineOffset: '-3px' } : {}) }}>Remarks</th>
                  <th onClick={() => pickColumn('shift_type')} title="Click to select the whole column" style={{ ...S.th, minWidth: '80px', cursor: 'pointer', ...(selectedColumn === 'shift_type' ? { outline: '3px solid #2563eb', outlineOffset: '-3px' } : {}) }}>Shift Type</th>
                  <th onClick={() => pickColumn('scale')} title="Click to select the whole column" style={{ ...S.th, minWidth: '90px', cursor: 'pointer', ...(selectedColumn === 'scale' ? { outline: '3px solid #2563eb', outlineOffset: '-3px' } : {}) }}>Scale (₹)</th>
                  <th style={{ ...S.th, minWidth: '115px' }}>Per Hr (₹)</th>
                  <th style={{ ...S.th, minWidth: '72px'  }}>No. Hrs</th>
                  <th onClick={() => pickColumn('salary')} title="Click to select the whole column" style={{ ...S.th, ...S.salaryTh, minWidth: '95px', cursor: 'pointer', ...(selectedColumn === 'salary' ? { outline: '3px solid #2563eb', outlineOffset: '-3px' } : {}) }}>Salary (₹)</th>
                  <th onClick={() => pickColumn('adv_date')} title="Click to select the whole column" style={{ ...S.th, ...S.deductionHeader, minWidth: '85px', cursor: 'pointer', ...(selectedColumn === 'adv_date' ? { outline: '3px solid #2563eb', outlineOffset: '-3px' } : {}) }}>Date</th>
                  <th onClick={() => pickColumn('tid')} title="Click to select the whole column" style={{ ...S.th, ...S.deductionHeader, minWidth: '110px', cursor: 'pointer', ...(selectedColumn === 'tid' ? { outline: '3px solid #2563eb', outlineOffset: '-3px' } : {}) }}>TID</th>
                  <th onClick={() => pickColumn('adv_amount')} title="Click to select the whole column" style={{ ...S.th, ...S.deductionHeader, minWidth: '95px', cursor: 'pointer', ...(selectedColumn === 'adv_amount' ? { outline: '3px solid #2563eb', outlineOffset: '-3px' } : {}) }}>Amount (₹)</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, idx) => {
                  const isSunday = row.day === 'Sunday';
                  const isEven   = idx % 2 === 0;
                  const isPicked = selectedRows.has(idx);
                  // Selection highlight wins while a row is actively selected, so
                  // the "you just clicked this" feedback is never hidden by its
                  // own custom color.
                  const rowBg    = isPicked ? '#dbeafe' : (row.row_color || (isEven ? 'white' : '#f8fafc'));

                  return (
                    <tr
                      key={idx}
                      onMouseDown={(e) => startDrag(idx, e)}
                      onMouseEnter={() => extendDrag(idx)}
                      style={{
                        backgroundColor: rowBg,
                        transition: 'background 0.1s',
                        cursor: 'cell',
                        userSelect: dragging.current ? 'none' : 'auto',
                      }}
                    >

                      {/* Date — display only */}
                      <td style={{ ...S.td, fontWeight: 600, color: '#0f172a', whiteSpace: 'nowrap', textAlign: 'center' }}>
                        {row.date}
                      </td>

                      {/* Day — display only, Sunday red */}
                      <td style={{ ...S.td, fontWeight: isSunday ? 700 : 500, color: isSunday ? '#dc2626' : '#475569', whiteSpace: 'nowrap' }}>
                        {row.day}
                      </td>

                      {/* Department */}
                      <td onMouseDown={(e) => startCellSel('department', idx, e)} onMouseEnter={() => extendCellSel('department', idx)} style={{ ...S.td, ...((selectedColumn === 'department' && selectedCells.has(idx)) ? { backgroundColor: '#bfdbfe' } : {}) }}>
                        <input
                          type="text"
                          style={S.input}
                          value={row.department}
                          ref={registerCell('department', idx)}
                          onChange={(e) => handleChange(idx, 'department', e.target.value)}
                          onKeyDown={commitSelection}
                        />
                      </td>

                      {/* Remarks — picked from the shared list, not typed here. Drag
                          across cells like Department to select a range; clicking
                          any cell in that range then applies the picked remark to
                          every selected row, not just the one clicked. */}
                      <td
                        onMouseDown={(e) => startCellSel('remarks', idx, e)}
                        onMouseEnter={() => extendCellSel('remarks', idx)}
                        style={{ ...S.td, ...((selectedColumn === 'remarks' && selectedCells.has(idx)) ? { backgroundColor: '#bfdbfe' } : {}) }}
                      >
                        <input
                          type="text"
                          readOnly
                          title={row.remarks || 'Click to pick a remark'}
                          style={{ ...S.input, cursor: 'pointer', ...(row.remarks_color ? { color: row.remarks_color, fontWeight: 700 } : {}) }}
                          value={row.remarks}
                          onClick={() => openRemarksPicker(idx)}
                        />
                      </td>

                      {/* Shift Type */}
                      <td onMouseDown={(e) => startCellSel('shift_type', idx, e)} onMouseEnter={() => extendCellSel('shift_type', idx)} style={{ ...S.td, ...((selectedColumn === 'shift_type' && selectedCells.has(idx)) ? { backgroundColor: '#bfdbfe' } : {}) }}>
                        <input
                          type="text"
                          style={S.input}
                          value={row.shift_type}
                          ref={registerCell('shift_type', idx)}
                          onChange={(e) => handleChange(idx, 'shift_type', e.target.value)}
                          onKeyDown={commitSelection}
                        />
                      </td>

                      {/* Scale */}
                      <td onMouseDown={(e) => startCellSel('scale', idx, e)} onMouseEnter={() => extendCellSel('scale', idx)} style={{ ...S.td, ...((selectedColumn === 'scale' && selectedCells.has(idx)) ? { backgroundColor: '#bfdbfe' } : {}) }}>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          style={S.input}
                          value={row.scale === 0 ? '' : row.scale}
                          placeholder="-"
                          ref={registerCell('scale', idx)}
                          onChange={(e) => handleChange(idx, 'scale', e.target.value)}
                          onKeyDown={commitSelection}
                        />
                      </td>

                      {/* Per Hr — 6 decimal places, auto-calculated but overridable */}
                      <td onMouseDown={(e) => startCellSel('per_hr', idx, e)} onMouseEnter={() => extendCellSel('per_hr', idx)} style={{ ...S.td, ...((selectedColumn === 'per_hr' && selectedCells.has(idx)) ? { backgroundColor: '#bfdbfe' } : {}) }}>
                        <input
                          type="number"
                          min="0"
                          step="0.000001"
                          style={S.input}
                          value={row.per_hr === 0 ? '' : row.per_hr.toFixed(6)}
                          placeholder="-"
                          ref={registerCell('per_hr', idx)}
                          onChange={(e) => handleChange(idx, 'per_hr', e.target.value)}
                          onKeyDown={commitSelection}
                          title="Auto-calculated: Scale ÷ Days ÷ 12. Manually overridable."
                        />
                      </td>

                      {/* No Hrs */}
                      <td onMouseDown={(e) => startCellSel('no_hrs', idx, e)} onMouseEnter={() => extendCellSel('no_hrs', idx)} style={{ ...S.td, ...((selectedColumn === 'no_hrs' && selectedCells.has(idx)) ? { backgroundColor: '#bfdbfe' } : {}) }}>
                        <input
                          type="number"
                          min="0"
                          step="0.5"
                          style={S.input}
                          value={row.no_hrs === 0 ? '' : row.no_hrs}
                          placeholder="-"
                          ref={registerCell('no_hrs', idx)}
                          onChange={(e) => handleChange(idx, 'no_hrs', e.target.value)}
                          onKeyDown={commitSelection}
                        />
                      </td>

                      {/* Salary — auto-calculated but overridable; anchor column */}
                      {/* salaryTd paints its own grey, which would hide the row
                          highlight — drop it while the row is selected. */}
                      <td onMouseDown={(e) => startCellSel('salary', idx, e)} onMouseEnter={() => extendCellSel('salary', idx)} style={{ ...S.td, ...((selectedColumn === 'salary' && selectedCells.has(idx)) ? { backgroundColor: '#bfdbfe' } : (isPicked ? {} : S.salaryTd)) }}>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          style={{ ...S.input, fontWeight: 600 }}
                          value={row.salary === 0 ? '' : row.salary}
                          placeholder="-"
                          ref={registerCell('salary', idx)}
                          onChange={(e) => handleChange(idx, 'salary', e.target.value)}
                          onKeyDown={commitSelection}
                          title="Auto-calculated: Per Hr × No. Hrs. Manually overridable."
                        />
                      </td>

                      {/* Adv Date */}
                      <td onMouseDown={(e) => startCellSel('adv_date', idx, e)} onMouseEnter={() => extendCellSel('adv_date', idx)} style={{ ...S.td, ...((selectedColumn === 'adv_date' && selectedCells.has(idx)) ? { backgroundColor: '#bfdbfe' } : {}) }}>
                        <input
                          type="text"
                          style={S.input}
                          value={row.adv_date}
                          placeholder="—"
                          ref={registerCell('adv_date', idx)}
                          onChange={(e) => handleChange(idx, 'adv_date', e.target.value)}
                          onKeyDown={commitSelection}
                        />
                      </td>

                      {/* TID */}
                      <td onMouseDown={(e) => startCellSel('tid', idx, e)} onMouseEnter={() => extendCellSel('tid', idx)} style={{ ...S.td, ...((selectedColumn === 'tid' && selectedCells.has(idx)) ? { backgroundColor: '#bfdbfe' } : {}) }}>
                        <input
                          type="text"
                          style={S.input}
                          value={row.tid}
                          placeholder="—"
                          ref={registerCell('tid', idx)}
                          onChange={(e) => handleChange(idx, 'tid', e.target.value)}
                          onKeyDown={commitSelection}
                        />
                      </td>

                      {/* Adv Amount — show empty when 0 (advances are optional) */}
                      <td onMouseDown={(e) => startCellSel('adv_amount', idx, e)} onMouseEnter={() => extendCellSel('adv_amount', idx)} style={{ ...S.td, ...((selectedColumn === 'adv_amount' && selectedCells.has(idx)) ? { backgroundColor: '#bfdbfe' } : {}) }}>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          style={S.input}
                          value={row.adv_amount === 0 ? '' : row.adv_amount}
                          placeholder="—"
                          ref={registerCell('adv_amount', idx)}
                          onChange={(e) => handleChange(idx, 'adv_amount', e.target.value)}
                          onKeyDown={commitSelection}
                        />
                      </td>
                    </tr>
                  );
                })}

                {/* ---- Sub-totals row (yellow) ---- */}
                <tr style={S.subtotalRow}>
                  <td colSpan={8} style={{ ...S.td, textAlign: 'right', paddingRight: '1rem' }}>Sub Totals:</td>
                  <td style={S.td}>₹ {Math.round(salarySum).toLocaleString()}</td>
                  <td colSpan={2} style={{ ...S.td, textAlign: 'right', paddingRight: '1rem' }}>Sub Total Deductions:</td>
                  <td style={{ ...S.td, color: '#991b1b' }}>₹ {totalAdvances.toLocaleString()}</td>
                </tr>

              </tbody>
            </table>
            </div>

            {/* Salary summary beside the table (was a stack of full-width rows
                below it) so all day rows fit without vertical scroll. Same
                fields as the nurse Salary Sheet, minus Hostel Fees — management
                staff don't have that deduction. */}
            <div style={S.summaryPanel}>
              <StaffPhoto path={photoUrl} name={staffName} />
              <style>{`.mgmt-subtotal-input::placeholder{color:#451a03;opacity:1;}`}</style>
              <div style={{ ...S.sumRow, backgroundColor: '#fcd34d', borderBottom: '2px solid #d97706' }}>
                <span style={{ ...S.sumLabel, color: '#451a03', fontWeight: 800 }} title="Sum of the Salary column — type over it to override">Sub Total</span>
                <input
                  type="number"
                  className="mgmt-subtotal-input"
                  value={subtotalDraft ?? subtotalOverride ?? Math.round(salarySum)}
                  onFocus={(e) => { setSubtotalDraft(String(subtotalOverride ?? Math.round(salarySum))); e.target.select(); }}
                  onChange={(e) => { clearGrossOverride(); setSubtotalDraft(e.target.value); setSubtotalOverride(e.target.value === '' ? null : Number(e.target.value)); }}
                  onBlur={() => { setSubtotalDraft(null); if (subtotalOverride !== null && subtotalOverride === Math.round(salarySum)) setSubtotalOverride(null); }}
                  style={{ ...S.sumInput, color: subtotalOverride !== null ? '#b45309' : '#1e3a8a' }}
                  placeholder={Math.round(salarySum).toString()}
                  title={subtotalOverride !== null ? `Overridden — calculated total is ₹ ${Math.round(salarySum).toLocaleString()}. Clear to go back to it.` : undefined}
                />
              </div>
              <div style={S.sumRow}>
                <span style={S.sumLabel}>HRA</span>
                <select value={hraEnabled ? 'Yes' : 'No'} onChange={(e) => { clearGrossOverride(); setHraEnabled(e.target.value === 'Yes'); }} style={S.sumSelect}><option value="Yes">Yes</option><option value="No">No</option></select>
                {hraEnabled ? <input type="number" value={hraValue === 0 ? '' : hraValue} onChange={(e) => { clearGrossOverride(); setHraValue(e.target.value === '' ? 0 : Number(e.target.value)); }} style={S.sumInput} placeholder="0" /> : <span style={S.sumVal}>₹ 0</span>}
              </div>
              <div style={S.sumRow}>
                <span style={S.sumLabel}>Allowance</span>
                <select value={allowanceEnabled ? 'Yes' : 'No'} onChange={(e) => { clearGrossOverride(); setAllowanceEnabled(e.target.value === 'Yes'); }} style={S.sumSelect}><option value="Yes">Yes</option><option value="No">No</option></select>
                {allowanceEnabled ? <input type="number" value={allowanceValue === 0 ? '' : allowanceValue} onChange={(e) => { clearGrossOverride(); setAllowanceValue(e.target.value === '' ? 0 : Number(e.target.value)); }} style={S.sumInput} placeholder="0" /> : <span style={S.sumVal}>₹ 0</span>}
              </div>
              <div style={{ ...S.sumRow, backgroundColor: '#bfdbfe' }}>
                <span style={S.sumLabel} title="Sub Total + HRA + Allowance. Type here to set it by hand.">Gross Salary</span>
                {/* Prefilled with the computed figure rather than left blank behind
                    a placeholder, so the real number is in the box and can be
                    selected and typed over. Emptying it hands the row back to
                    Sub Total + HRA + Allowance. This is the figure the P&L reads
                    for this staff member. */}
                <input
                  type="number"
                  min="0"
                  step="1"
                  value={grossDraft !== null ? grossDraft : Math.round(grossSalary)}
                  onFocus={(e) => {
                    setGrossDraft(String(Math.round(grossSalary)));
                    // Whole figure preselected so typing replaces it outright.
                    // Deferred: React re-renders with the draft value first, and
                    // setting the value wipes a selection made before it lands.
                    const el = e.target as HTMLInputElement;
                    setTimeout(() => el.select(), 0);
                  }}
                  onChange={(e) => setGrossDraft(e.target.value)}
                  onBlur={() => {
                    const raw = (grossDraft ?? '').trim();
                    const parsed = parseFloat(raw);
                    // Emptied, or left sitting on the computed figure -> nothing
                    // was really typed, so keep tracking the sheet. Compared at
                    // whole rupees, which is what the box showed.
                    setGrossOverride(
                      raw === '' || !Number.isFinite(parsed) || Math.round(parsed) === Math.round(computedGross)
                        ? null
                        : Math.round(parsed),
                    );
                    setGrossDraft(null);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
                    if (e.key === 'Escape') { setGrossDraft(null); (e.target as HTMLInputElement).blur(); }
                  }}
                  // Wider than the shared sumInput: a gross runs to six figures.
                  style={{ ...S.sumInput, width: '96px', fontWeight: 700 }}
                  title="Auto-calculated from Sub Total + HRA + Allowance. Type to set it by hand; clear the box to go back."
                />
              </div>
              <div style={S.sumRow}>
                <span style={S.sumLabel}>Professional Tax</span>
                <select value={ptEnabled ? 'Yes' : 'No'} onChange={(e) => setPtEnabled(e.target.value === 'Yes')} style={S.sumSelect}><option value="Yes">Yes</option><option value="No">No</option></select>
                {ptEnabled ? <input type="number" value={ptValue === 0 ? '' : ptValue} onChange={(e) => setPtValue(e.target.value === '' ? 0 : Number(e.target.value))} style={{ ...S.sumInput, color: '#991b1b' }} placeholder="0" /> : <span style={{ ...S.sumVal, color: '#991b1b' }}>- ₹ 0</span>}
              </div>
              <div style={S.sumRow}>
                <span style={S.sumLabel}>TDS</span>
                <select value={tdsEnabled ? 'Yes' : 'No'} onChange={(e) => { const on = e.target.value === 'Yes'; setTdsEnabled(on); if (on && tdsPercent === 0) setTdsPercent(10); }} style={S.sumSelect}><option value="Yes">Yes</option><option value="No">No</option></select>
                {tdsEnabled && <input type="number" min="0" max="100" value={tdsPercent === 0 ? '' : tdsPercent} onChange={(e) => setTdsPercent(e.target.value === '' ? 0 : Number(e.target.value))} style={{ width: '34px', padding: '2px', textAlign: 'center', border: '1px solid #cbd5e1', borderRadius: '4px', fontSize: '0.72rem' }} placeholder="%" />}
                <span style={{ ...S.sumVal, color: '#991b1b' }}>₹ {fmt(tdsAmount)}</span>
              </div>
              <div style={S.sumRow}>
                <span style={S.sumLabel} title="Advances entered against the deduction columns">Advances</span>
                <span style={{ ...S.sumVal, color: '#991b1b' }}>₹ {fmt(totalAdvances)}</span>
              </div>
              <div style={S.sumRow}>
                <span style={S.sumLabel}>Total Deductions</span>
                <span style={{ ...S.sumVal, color: '#991b1b' }}>₹ {fmt(totalDeductions)}</span>
              </div>
              <div style={{ ...S.sumRow, backgroundColor: '#86efac', borderBottom: 'none' }}>
                <span style={{ ...S.sumLabel, color: '#14532d', fontSize: '0.85rem' }}>Net Take Home</span>
                <span style={{ ...S.sumVal, color: '#14532d', fontSize: '1rem' }}>₹ {fmtWhole(netTakeHome)}</span>
              </div>

              {/* Bank details — saved on the staff record via PATCH /management-staff */}
              <div style={S.bankHeader}>
                <span style={{ flex: 1 }}>Bank Details</span>
                {bankEditing ? (
                  <>
                    <button type="button" style={S.bankBtn} onClick={() => setBankEditing(false)} disabled={bankSaving}>Cancel</button>
                    <button type="button" style={{ ...S.bankBtnPrimary, cursor: bankSaving ? 'wait' : 'pointer' }} onClick={saveBankDetails} disabled={bankSaving}>{bankSaving ? 'Saving…' : 'Save'}</button>
                  </>
                ) : (
                  <button type="button" style={S.bankBtn} onClick={startBankEdit} title="Edit bank details for this staff">Edit</button>
                )}
              </div>
              {MGMT_BANK_FIELDS.map((f) => (
                <div key={f.key} style={S.bankRow}>
                  <span style={S.bankLabel}>{f.label}</span>
                  {bankEditing ? (
                    <input
                      type="text"
                      value={bankDraft[f.key] ?? ''}
                      onChange={(e) => setBankDraft((prev) => ({ ...prev, [f.key]: e.target.value }))}
                      style={S.bankInput}
                      placeholder="—"
                    />
                  ) : (
                    <span style={S.bankValue}>{bank[f.key] || '—'}</span>
                  )}
                </div>
              ))}
              {/* Sum of just the currently drag-selected range within a
                  summable column — separate from Sub Totals/Gross Salary,
                  which always cover the whole month. */}
              {selectedColumnSum !== null && (
                <div style={{ ...S.sumRow, backgroundColor: '#eef2ff' }}>
                  <span style={S.sumLabel}>Selected: {FILL_COLUMN_LABELS[selectedColumn as string]} ({selectedCells.size})</span>
                  <span style={{ ...S.sumVal, color: '#3730a3' }}>{Math.round(selectedColumnSum).toLocaleString('en-IN')}</span>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* ---- Remarks picker — rendered at page root; the sheet card is overflow:hidden ---- */}
      {remarksRow !== null && rows[remarksRow] && (
        <ReferenceListModal
          title="Remarks"
          subtitle={
            remarksBulkTargets
              ? `Applies to ${remarksBulkTargets.size} selected rows`
              : `${rows[remarksRow].date} · ${rows[remarksRow].day}`
          }
          category={REMARKS_CATEGORY}
          value={rows[remarksRow].remarks}
          onSelect={(label) => {
            // Use the snapshot taken when the picker opened, not the live
            // selection — see the remarksBulkTargets comment above for why.
            // Clearing the remark (label === '') also clears the row's
            // highlight color and text color — an empty remark shouldn't be
            // left looking flagged.
            if (remarksBulkTargets) {
              setRows((prev) => prev.map((r, i) => (remarksBulkTargets.has(i)
                ? { ...r, remarks: label, ...(label === '' ? { row_color: null, remarks_color: null } : {}) }
                : r)));
            } else if (label === '') {
              setRows((prev) => prev.map((r, i) => (i === remarksRow ? { ...r, remarks: '', row_color: null, remarks_color: null } : r)));
            } else {
              handleChange(remarksRow, 'remarks', label);
            }
            // Deliberately NOT clearing remarksBulkTargets here — after a bulk
            // Remarks pick, the highlight-color toolbar below stays keyed to
            // the same rows so coloring them is the very next, one-click step.
            setRemarksRow(null);
          }}
          onClose={() => setRemarksRow(null)}
        />
      )}


      {/* Selection indicator — the fill happens by typing into a cell, which is
          not self-evident, so say so while a multi-row selection is live. Also
          doubles as the highlight-color toolbar: it stays keyed to whichever
          rows were most recently bulk-filled with a Remarks value, so picking
          a remark and then a color reads as one continuous action, or falls
          back to a plain whole-row drag-selection otherwise. */}
      <div ref={toolbarRef}>
      {colorTargets && colorTargets.size >= 1 && (
        <div
          style={{
            position: 'fixed', bottom: 18, left: '50%', transform: 'translateX(-50%)',
            backgroundColor: '#1e3a8a', color: 'white', padding: '0.5rem 0.95rem',
            borderRadius: '999px', fontSize: '0.78rem', fontWeight: 600, zIndex: 2000,
            boxShadow: '0 6px 18px rgba(0,0,0,0.22)', display: 'flex', alignItems: 'center', gap: '0.7rem',
          }}
        >
          {colorTargets.size} row{colorTargets.size > 1 ? 's' : ''} selected
          {remarksBulkTargets ? ' (from Remarks fill)' : (selectedRows.size > 1 && ' — type in any number column to set them all')}
          <span style={{ opacity: 0.6 }}>|</span>
          <span style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
            Highlight:
            {ROW_HIGHLIGHT_COLORS.map((c) => (
              <button
                key={c.value}
                type="button"
                title={c.label}
                onClick={() => applyRowColor(c.value)}
                style={{ width: '18px', height: '18px', borderRadius: '50%', backgroundColor: c.value, border: '1px solid rgba(255,255,255,0.6)', cursor: 'pointer', padding: 0 }}
              />
            ))}
            <button
              type="button"
              title="Clear highlight"
              onClick={() => applyRowColor(null)}
              style={{ width: '18px', height: '18px', borderRadius: '50%', backgroundColor: 'transparent', border: '1px solid rgba(255,255,255,0.6)', cursor: 'pointer', padding: 0, position: 'relative' }}
            >
              <span style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px', lineHeight: 1 }}>✕</span>
            </button>
          </span>
          <span style={{ opacity: 0.6 }}>|</span>
          <span style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
            Text:
            {TEXT_HIGHLIGHT_COLORS.map((c) => (
              <button
                key={c.value}
                type="button"
                title={c.label}
                onClick={() => applyRemarksColor(c.value)}
                style={{ width: '18px', height: '18px', borderRadius: '4px', backgroundColor: 'white', border: `1px solid ${c.value}`, cursor: 'pointer', padding: 0, color: c.value, fontSize: '11px', fontWeight: 800, lineHeight: '16px' }}
              >A</button>
            ))}
            <button
              type="button"
              title="Clear text color"
              onClick={() => applyRemarksColor(null)}
              style={{ width: '18px', height: '18px', borderRadius: '4px', backgroundColor: 'transparent', border: '1px solid rgba(255,255,255,0.6)', cursor: 'pointer', padding: 0, position: 'relative' }}
            >
              <span style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px', lineHeight: 1 }}>✕</span>
            </button>
          </span>
          <span style={{ opacity: 0.6 }}>|</span>
          <button
            type="button"
            onClick={() => { setSelectedRows(new Set()); setRemarksBulkTargets(null); clearColumnSel(); }}
            style={{ background: 'rgba(255,255,255,0.15)', border: 'none', color: 'white', borderRadius: '999px', padding: '3px 10px', fontSize: '0.75rem', fontWeight: 700, cursor: 'pointer' }}
          >
            Done
          </button>
        </div>
      )}

      {selectedColumn && selectedCells.size > 0 && (
        <div
          style={{
            position: 'fixed', bottom: 18, left: '50%', transform: 'translateX(-50%)',
            backgroundColor: '#1e3a8a', color: 'white', padding: '0.5rem 0.95rem',
            borderRadius: '999px', fontSize: '0.78rem', fontWeight: 600, zIndex: 2000,
            boxShadow: '0 6px 18px rgba(0,0,0,0.22)', display: 'flex', alignItems: 'center', gap: '0.6rem',
          }}
        >
          {selectedCells.size} <b>{FILL_COLUMN_LABELS[selectedColumn as string]}</b> cell{selectedCells.size > 1 ? 's' : ''} selected — type a value to fill {selectedCells.size > 1 ? 'them' : 'it'}
          <span style={{ opacity: 0.8 }}>· Esc to clear</span>
        </div>
      )}
      </div>
    </div>
  );
};

export default ManagementDailySalarySheet;
